import { useAppSettingsStore } from "./stores/AppSettingsStore";
import { isQrLoginDisabledOnWeb } from "./utils/featureFlags";
import { AppUpdateAvailability } from "@capawesome/capacitor-app-update";
import { RouteRecordRaw, createRouter, createWebHistory } from "vue-router";
import { useUserManagerStore } from "./stores/userManagerStore";
import { useSalesReportsStore } from "./stores/salesReports";
import { useNewComponentStore } from "./stores/newComponentStore";
import { usePageTransitionsStore } from "./stores/pageTransitionsStore";
import OauthCallback from "./pages/OauthCallback.vue";
import log from "./utils/logger";
const DashboardLayout = () => import("./pages/dashboard/DashboardLayout.vue");
const DashboardOverview = () => import("./pages/dashboard/DashboardOverview.vue");
const DashboardDetail = () => import("./pages/dashboard/DashboardDetail.vue");
import CenterSigninShell from "./pages/CenterSigninShell.vue";
import CenterSignin from "./pages/CenterSignin.vue";
import CenterUserSignin from "./pages/CenterUserSignin.vue";
const NoCenter = () => import("./pages/NoCenter.vue");
const QrLogin = () => import("./pages/QrLogin.vue");
const SessionExpired = () => import("./pages/SessionExpired.vue");
const WebLoginRequired = () => import("./pages/WebLoginRequired.vue");
const ExternalLogin = () => import("./pages/ExternalLogin.vue");
const AuthLayout = () => import("./pages/AuthLayout.vue");
import Profile from "./pages/Profile.vue";
import ProfileLayout from "./pages/ProfileLayout.vue";
import Main from "./pages/Main.vue";
import Center from "./pages/Center.vue";
import NewComponent from "./pages/NewComponent/NewComponent.vue";
import NewComponentType from "./pages/NewComponent/NewComponentType.vue";
import NewComponentDate from "./pages/NewComponent/NewComponentDate.vue";
import NewComponentDataset from "./pages/NewComponent/NewComponentDataset.vue";
import NewComponentChart from "./pages/NewComponent/NewComponentChart.vue";
import ProfileCenter from "./pages/Profile/Center.vue";
import ProfileSecurity from "./pages/Profile/Security.vue";
import CenterReport from "./pages/Center/CenterReport.vue";
import ReportPage from "./pages/ReportPage.vue";
import ComponentEdit from "./pages/Profile/ComponentEdit.vue";
import CenterSalesReport from "./pages/Center/Families/CenterSalesReport.vue";
import CenterFamiliesReport from "./pages/Center/Families/CenterFamiliesReport.vue";
import FamilyDetailReport from "./pages/Center/Families/FamilyDetailReport.vue";
import CustomChartsPage from "./pages/CustomCharts/CustomChartsPage.vue";
const CenterClientTypesReport = () => import("./pages/Center/CenterClientTypesReport.vue");
const CenterClientsReport = () => import("./pages/Center/CenterClientsReport.vue");
const CenterMembersReport = () => import("./pages/Center/CenterMembersReport.vue");
import UpdateAvailable from "./pages/UpdateAvailable.vue";
import DisplaySettings from "./pages/Profile/DisplaySettings.vue";
import About from "./pages/Profile/About.vue";
const ProfileQrLogin = () => import("./pages/Profile/QrLogin.vue");

// Kill-switch: set VITE_WEB_FORCE_CLIENT_AREA=true in the env to funnel web users
// to WebLoginRequired (client-area CTA) instead of CenterSignin / NoCenterPage.
// Unset (default) → internal login paths are accessible on web.
const forceClientAreaOnWeb = import.meta.env.VITE_WEB_FORCE_CLIENT_AREA === 'true'


const componentsEditingChildrens = [
    {
        path: '',
        name: 'NewComponentType',
        component: NewComponentType,
    },
    {
        path: 'dates',
        name: 'NewComponentDates',
        component: NewComponentDate,
    },
    {
        path: 'datasets',
        name: 'NewComponentDatasets',
        component: NewComponentDataset,
    },
    {
        path: 'chart',
        name: 'NewComponentChart',
        component: NewComponentChart,
    }
]


const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        component: Main,
        redirect: { name: 'Dashboard' },
        children: [
            {
                path: 'dashboard',
                component: DashboardLayout,
                meta: { requiresCenters: true },
                beforeEnter: () => {
                    const userManagerStore = useUserManagerStore();
                    const centers = userManagerStore.displayedCenters;
                    if (centers.length === 1) {
                        return {
                            name: 'Center',
                            params: { centerId: centers[0].id.toString() }
                        };
                    }
                },
                children: [
                    {
                        path: '',
                        name: 'Dashboard',
                        component: DashboardOverview,
                    },
                    {
                        path: 'detail/:reportId?',
                        name: 'DashboardDetail',
                        component: DashboardDetail,
                        beforeEnter: () => {
                            if (!window.matchMedia('(min-width: 1280px)').matches) {
                                return { name: 'Dashboard' }
                            }
                        },
                    },
                ],
            },
            {
                path: 'center/:centerId',
                name: 'Center',
                component: Center,
                meta: { requiresCenters: true }
            },
        ],
        beforeEnter: async (to) => {
            log.debug(`[GO-DEBUG][MAIN-GUARD] Main beforeEnter → route="${String(to.name)}", refresh=${to.query.refresh}`)
            const salesReportsStore = useSalesReportsStore()
            const appStore = useAppSettingsStore();

            // appStore.startLoadingPage(3)

            if (to.query.refresh === 'true') {
                log.debug(`[GO-DEBUG][MAIN-GUARD] refresh=true → re-fetching user + components`)
                appStore.advance()
                const userManagerStore = useUserManagerStore();
                await userManagerStore.getUserInfo()
                appStore.advance()
                await salesReportsStore.initCenterComponents()
            }

            // await salesReportsStore.fetchAllSalesReports();
            appStore.advance()
            log.debug(`[GO-DEBUG][MAIN-GUARD] Main beforeEnter done`)
        }
    },
    {
        path: '/report/:id',
        name: 'Report',
        component: ReportPage,
    },
    {
        path: '/center/:centerId/report/:id',
        name: 'CenterReports',
        component: CenterReport,
    },
    {

        path: '/center/:centerId/report/:id/sales',
        name: 'CenterReport',
        component: CenterSalesReport,
    },
    {

        path: '/center/:centerId/report/:id/families',
        name: 'CenterReportFamilies',
        component: CenterFamiliesReport,
    },
    {
        path: '/center/:centerId/report/:id/families/:familyName',
        name: 'family-detail-report',
        component: FamilyDetailReport,
    },
    {
        path: '/center/:centerId/report/:id/client-types',
        name: 'CenterClientTypes',
        component: CenterClientTypesReport,
    },
    {
        path: '/center/:centerId/report/:id/clients',
        name: 'CenterClients',
        component: CenterClientsReport,
    },
    {
        path: '/center/:centerId/report/:id/members',
        name: 'CenterMembers',
        component: CenterMembersReport,
    },
    {
        path: '/center/:centerId/report/:id/custom-charts',
        name: 'CustomCharts',
        component: CustomChartsPage,
    },
    {
        path: '/new-components',
        name: 'NewComponent',
        redirect: { name: 'NewComponentType' },
        component: NewComponent,
        children: componentsEditingChildrens,
        beforeEnter: () => {
            const newComponentStore = useNewComponentStore();
            newComponentStore.reset();
        }
    },
    {
        path: '/profile',
        component: ProfileLayout,
        children: [
            {
                path: '',
                name: 'Profile',
                component: Profile,
            },
            {
                path: 'display',
                name: 'DisplaySettings',
                component: DisplaySettings,
            },
            {
                path: 'language',
                name: 'Language',
                redirect: { name: 'DisplaySettings' },
            },
            {
                path: 'security',
                name: 'Security',
                component: ProfileSecurity,
            },
            {
                path: 'centers/:id',
                name: 'ProfileCenter',
                component: ProfileCenter,
            },
            {
                path: 'components/:id',
                name: 'Component',
                component: Profile,
            },
            {
                path: 'components/:id/edit',
                name: 'ComponentEdit',
                component: ComponentEdit,
                beforeEnter: async (to) => {
                    const newComponentStore = useNewComponentStore();
                    newComponentStore.loadEditing(to.params.id as string)
                }
            },
            {
                path: 'components/:id/display',
                name: 'ComponentDisplay',
                component: Profile,
            },
            {
                path: 'qr-login',
                name: 'ProfileQrLogin',
                component: ProfileQrLogin,
                beforeEnter: () => {
                    if (isQrLoginDisabledOnWeb()) {
                        return { name: 'Profile' }
                    }
                },
            },
            {
                path: 'about',
                name: 'About',
                component: About,
            },
        ],
    },
    {
        path: '/oauth2/callback',
        name: 'Callback',
        meta: { public: true },
        component: OauthCallback
    },
    {
        path: '/authenticate/external',
        name: 'ExternalLogin',
        meta: { public: true },
        component: ExternalLogin
    },
    {
        path: '/auth',
        component: AuthLayout,
        children: [
            {
                path: '/no-center',
                name: 'NoCenterPage',
                component: NoCenter,
            },
            {
                path: '/qr-login',
                name: 'QrLogin',
                component: QrLogin,
                meta: { public: true },
                beforeEnter: () => {
                    if (isQrLoginDisabledOnWeb()) {
                        return { name: 'NoCenterPage' }
                    }
                },
            },
            {
                path: '/session-expired',
                name: 'SessionExpired',
                component: SessionExpired,
                meta: { public: true },
            },
            {
                path: '/web-login-required',
                name: 'WebLoginRequired',
                component: WebLoginRequired,
                meta: { public: true },
            },
            {
                path: '/center/signin',
                component: CenterSigninShell,
                beforeEnter: () => {
                    const appStore = useAppSettingsStore();
                    if (appStore.plateform === 'web' && forceClientAreaOnWeb) {
                        return { name: 'WebLoginRequired' }
                    }
                },
                children: [
                    {
                        path: '',
                        name: 'CenterSignin',
                        component: CenterSignin,
                    },
                    {
                        path: '/center/user/signin',
                        name: 'CenterUserSignin',
                        component: CenterUserSignin,
                    },
                ],
            },
        ],
    },
    {
        path: '/update-available',
        name: 'UpdateAvailable',
        component: UpdateAvailable,
        meta: {
            bypassBeforeEach: true
        }
    }
]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
})

router.beforeEach(async (to) => {
    log.debug(`[GO-DEBUG][GUARD] ▶ beforeEach START → route="${String(to.name)}" path="${to.path}"`)

    if (to.meta.bypassBeforeEach) {
        log.debug(`[GO-DEBUG][GUARD] bypassBeforeEach → skipping guard`)
        return;
    }

    const appSettingsStore = useAppSettingsStore();
    if (appSettingsStore.updateInfo?.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
        log.debug(`[GO-DEBUG][GUARD] update available → redirecting to UpdateAvailable`)
        return {
            name: 'UpdateAvailable'
        }
    }

    const userManagerStore = useUserManagerStore();

    if (!to.meta.public) {
        log.debug(`[GO-DEBUG][GUARD] route is not public, checking auth...`)
        log.debug(`[GO-DEBUG][GUARD] store.token=${!!userManagerStore.token}, store.user=${!!userManagerStore.user}`)

        const token = await userManagerStore.getToken
        log.debug(`[GO-DEBUG][GUARD] getToken result: ${token ? 'exists' : 'null'}`)
        if (!token) {
            log.debug(`[GO-DEBUG][GUARD] ✗ no token → signinRedirect`)
            await userManagerStore.signinRedirect()
            return false;
        }

        let tokenRefreshed = false;
        const tokenValid = userManagerStore.validateAccessToken(token)
        log.debug(`[GO-DEBUG][GUARD] token valid=${tokenValid}`)
        if (!tokenValid) {
            try {
                log.debug(`[GO-DEBUG][GUARD] token invalid → signinSilent...`)
                await userManagerStore.signinSilent()
                log.debug(`[GO-DEBUG][GUARD] signinSilent OK`)
            } catch (e) {
                log.debug(`[GO-DEBUG][GUARD] ✗ signinSilent failed → signinRedirect`, e)
                await userManagerStore.signinRedirect()
                return false;
            }
            tokenRefreshed = true;
        }

        if (!userManagerStore.token) {
            log.debug(`[GO-DEBUG][GUARD] store.token is null → updateTokenFromStorage...`)
            await userManagerStore.updateTokenFromStorage()
            log.debug(`[GO-DEBUG][GUARD] updateTokenFromStorage done, token=${!!userManagerStore.token}`)
            tokenRefreshed = true;
        }

        log.debug(`[GO-DEBUG][GUARD] tokenRefreshed=${tokenRefreshed}, user=${!!userManagerStore.user}`)
        if (tokenRefreshed || !userManagerStore.user) {
            try {
                log.debug(`[GO-DEBUG][GUARD] calling getUserInfo...`)
                await userManagerStore.getUserInfo()
                log.debug(`[GO-DEBUG][GUARD] getUserInfo OK, user=${!!userManagerStore.user}, centers=${userManagerStore.user?.centers?.length ?? 0}`)
                log.debug(`[GO-DEBUG][GUARD] calling initCenterComponents...`)
                await useSalesReportsStore().initCenterComponents()
                log.debug(`[GO-DEBUG][GUARD] initCenterComponents OK`)
            } catch (e) {
                log.debug(`[GO-DEBUG][GUARD] ✗ getUserInfo/initCenterComponents failed:`, e)
                const appStore = useAppSettingsStore();
                const forceWeb = appStore.plateform === 'web' && forceClientAreaOnWeb
                const noCenterTarget = forceWeb ? 'WebLoginRequired' : 'NoCenterPage'
                const allowedWithoutCenters: Array<RouteRecordRaw['name']> =
                    forceWeb
                        ? [noCenterTarget]
                        : ['NoCenterPage', 'CenterSignin', 'CenterUserSignin']
                if (!allowedWithoutCenters.includes(to.name)) {
                    log.debug(`[GO-DEBUG][GUARD] → redirecting to ${noCenterTarget}`)
                    return { name: noCenterTarget }
                }
            }
        }
    } else {
        log.debug(`[GO-DEBUG][GUARD] route is public, skipping auth`)
    }

    if (to.meta.requiresCenters) {
        log.debug(`[GO-DEBUG][GUARD] requiresCenters check: centers=${userManagerStore.user?.centers?.length ?? 'no user'}`)
        if (!userManagerStore.user?.centers || userManagerStore.user?.centers.length === 0) {
            const appStore = useAppSettingsStore();
            const target = (appStore.plateform === 'web' && forceClientAreaOnWeb) ? 'WebLoginRequired' : 'NoCenterPage'
            log.debug(`[GO-DEBUG][GUARD] → no centers, redirecting to ${target}`)
            return { name: target }
        }
    }

    // Redirect away from NoCenter if user already has centers
    if (to.name === 'NoCenterPage' && userManagerStore.user?.centers && userManagerStore.user.centers.length > 0) {
        log.debug(`[GO-DEBUG][GUARD] user has centers but on NoCenterPage → redirecting to Dashboard`)
        return { name: 'Dashboard' }
    }

    log.debug(`[GO-DEBUG][GUARD] ◼ beforeEach END → allowing navigation to "${String(to.name)}"`)
})

// Auto-detect navigation direction for transitions
let isPopNavigation = false;

window.addEventListener('popstate', () => {
  isPopNavigation = true;
});

router.beforeEach((_to, from) => {
  const transitions = usePageTransitionsStore();

  // Only auto-set if useAppRouter() hasn't already set a transition
  if (!transitions.transitionName && from.name) {
    const dir = isPopNavigation ? 'page-back' : 'page-forward';
    log.debug(`[GO-DEBUG][TRANSITION] auto-set transition="${dir}" (from="${String(from.name)}" → to="${String(_to.name)}")`)
    transitions.setTransition(dir);
  }

  isPopNavigation = false;
});


export default router